/**
 * A lightweight, rule-based intent matcher that stands in for a server-side LLM.
 * Given the user's text it returns an answer plus the most relevant actions
 * (rendered as one-click buttons) and follow-up suggestions. Swapping this for
 * a real `/api/copilot` streaming call later only touches this file.
 */
export interface CopilotResponse {
  text: string;
  actionIds?: string[];
  suggestions?: string[];
}

type Platform = 'whatsapp' | 'messenger' | 'instagram' | 'shopify' | 'woocommerce';

function detectPlatform(q: string): Platform | null {
  if (/whats\s?app|\bwa\b/.test(q)) return 'whatsapp';
  if (/messenger|facebook|\bfb\b|page/.test(q)) return 'messenger';
  if (/instagram|insta|\big\b|\bdm\b/.test(q)) return 'instagram';
  if (/shopify/.test(q)) return 'shopify';
  if (/woo(commerce)?|wordpress/.test(q)) return 'woocommerce';
  return null;
}

const CONNECT_ACTION: Record<Platform, string> = {
  whatsapp: 'connect-whatsapp',
  messenger: 'connect-messenger',
  instagram: 'connect-instagram',
  shopify: 'connect-shopify',
  woocommerce: 'connect-woocommerce',
};

export function respond(input: string): CopilotResponse {
  const q = input.toLowerCase().trim();

  // Greeting / "what can I do" / onboarding
  if (/^(hi|hey|hello|yo|salam|assalam)\b/.test(q) || /what.*(can|do).*(i|you)|how.*(help|work|start|use)|get(ting)? started|where.*start/.test(q)) {
    return {
      text:
        "I'm your OmnibizChat Copilot. This platform connects your customer channels — Messenger, Instagram, WhatsApp — and your store (Shopify/WooCommerce), then an AI auto-replies to shoppers using your product catalog and FAQs. I can explain anything or just set it up for you. A good first step is connecting a channel:",
      actionIds: ['connect-whatsapp', 'open-knowledge', 'open-automations'],
      suggestions: ['What is the Knowledge Base?', 'Connect my Shopify store', 'Show me my analytics'],
    };
  }

  // Connect a channel/store
  if (/connect|add|set\s?up|integrat|link/.test(q)) {
    const p = detectPlatform(q);
    if (p) {
      return {
        text: `Sure — I'll take you to the ${labelFor(p)} setup and highlight it for you.`,
        actionIds: [CONNECT_ACTION[p]],
      };
    }
    return {
      text: "Which channel or store would you like to connect? Pick one and I'll take you there:",
      actionIds: ['connect-whatsapp', 'connect-messenger', 'connect-instagram', 'connect-shopify', 'connect-woocommerce'],
    };
  }

  // Knowledge base
  if (/knowledge|catalog|product|price|faq|policy|return|import|csv/.test(q)) {
    return {
      text:
        "The Knowledge Base is what your AI uses to answer customers — your products (with prices & descriptions) and FAQs/policies. You can add products manually, import a CSV, or connect a Google Sheet. Want me to open it?",
      actionIds: ['import-products-csv', 'add-faq', 'open-knowledge'],
      suggestions: ['Re-index my knowledge base', 'Add a return policy'],
    };
  }

  // Automations
  if (/automat|auto.?reply|reply|business hour|hours|quick repl|rule|trigger|tone/.test(q)) {
    return {
      text:
        "Automations control how the AI replies — tone, business hours, canned quick-replies, and keyword rules. I can jump you straight to the rule builder or your hours:",
      actionIds: ['create-automation', 'set-business-hours', 'open-automations'],
    };
  }

  // Leads
  if (/lead|contact|export|crm/.test(q)) {
    return {
      text:
        "Leads are shoppers the AI has captured across your channels — name, contact, source and status. You can review or export them:",
      actionIds: ['export-leads', 'open-leads'],
    };
  }

  // Analytics
  if (/analytic|metric|report|performance|stat|automation rate|response time/.test(q)) {
    return {
      text:
        "Analytics shows conversation volume, AI automation rate, response times and per-channel performance over time. Let's take a look:",
      actionIds: ['open-analytics'],
      suggestions: ['How is automation rate measured?'],
    };
  }

  // Pricing / billing
  if (/pric|plan|cost|subscri|billing|upgrade/.test(q)) {
    return {
      text:
        "Your plan and billing live in Settings. I can take you there to review or change your plan.",
      actionIds: ['open-settings'],
    };
  }

  // Fallback
  return {
    text:
      "I can help you connect channels, manage your product knowledge, and configure how the AI replies. Here are a few things I can do right now:",
    actionIds: ['connect-whatsapp', 'import-products-csv', 'create-automation'],
    suggestions: ['What can I do here?', 'Connect a sales channel', 'Set up my product knowledge'],
  };
}

function labelFor(p: Platform): string {
  return {
    whatsapp: 'WhatsApp Business',
    messenger: 'Messenger',
    instagram: 'Instagram',
    shopify: 'Shopify',
    woocommerce: 'WooCommerce',
  }[p];
}

/** Context-aware chips shown above the input, keyed to the current page. */
export function suggestionsForRoute(path: string): string[] {
  if (path.startsWith('/integrations')) return ['Connect WhatsApp', "What's the difference between channels?", 'Connect my Shopify store'];
  if (path.startsWith('/knowledge')) return ['Import products from CSV', 'Add an FAQ', 'What is the Knowledge Base?'];
  if (path.startsWith('/automations')) return ['Set my business hours', 'Create an automation rule', 'How do auto-replies work?'];
  if (path.startsWith('/leads')) return ['Export my leads', 'What counts as a lead?'];
  if (path.startsWith('/analytics')) return ['Explain these metrics', 'How is automation rate measured?'];
  if (path.startsWith('/settings')) return ['Update my business profile', 'Where do I manage billing?'];
  if (path.startsWith('/conversations')) return ['How does the AI reply?', 'Set up my product knowledge'];
  return ['What can I do here?', 'Connect a sales channel', 'Set up my product knowledge'];
}
